'use client';

import type { CauseDetail } from '@dte/otw-azure-functions/src/common/interfaces/api/messageDetail/CauseDetail';
import type { PremiseStatusV2 } from '@dte/otw-azure-functions/src/common/interfaces/api/PremiseStatusV2';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { type ReactNode, useState } from 'react';
import { MessageDetailDisplay } from '../../../../../../components/SearchPremises/MessageDetailDisplay';
import { Spinner } from '../../../../../../components/Spinner/Spinner';
import { AuthenticatedJsonContent } from '../../../content/jsonGet/AuthenticatedJsonContent';
import { type PropsWithPremiseId, usePremiseId } from '../usePremiseId';
import { getPremiseStatusURL } from './getPremiseStatusURL';
import { PremiseSectionHeader } from './PremiseSectionHeader';
import { JsonModal } from '../../../../../../components/HighlightJson/HighlightJson';
import { AppAction } from '../../../../../../contexts/TokenContext/Roles';
import { RestrictedContent } from '../../../content/restricted/RestrictedContent';

function transformCauseDetails(causeDetails?: CauseDetail): CauseDetail | undefined {
	if (!causeDetails) {
		return undefined;
	}

	// Omit details from displaying (longer text is only used for IVR) and use the message instead
	causeDetails.details = causeDetails.message;
	delete causeDetails.message;

	return causeDetails;
}

function DebugPremiseStatusButton({ status }: { status?: PremiseStatusV2 }): ReactNode {
	if (!status) {
		return null;
	}

	return (
		<RestrictedContent action={AppAction.Debug}>
			<div style={{ float: 'right' }}>
				<JsonModal value={status} title="Debug Premise Status" />
			</div>
		</RestrictedContent>
	);
}

function PremiseStatusDisplayContent({ status }: { status?: PremiseStatusV2 }): ReactNode {
	if (!status) {
		return <Spinner />;
	}

	const causeDetails = transformCauseDetails(copyJson(status.cause));

	return (
		<section style={{ marginTop: '1rem', minHeight: '10rem' }}>
			<PremiseSectionHeader>Status displayed in self-service channels</PremiseSectionHeader>
			<DebugPremiseStatusButton status={status} />
			<MessageDetailDisplay messageDetail={status?.empathy} />
			<MessageDetailDisplay messageDetail={status?.status} />
			<MessageDetailDisplay messageDetail={status?.estimate}>Estimated Restoration</MessageDetailDisplay>
			<MessageDetailDisplay messageDetail={status?.crew}>Crew</MessageDetailDisplay>
			<MessageDetailDisplay messageDetail={causeDetails}>Cause</MessageDetailDisplay>
			<MessageDetailDisplay messageDetail={status?.engineering} />
		</section>
	);
}

export function PremiseStatusDisplay({ premiseId }: PropsWithPremiseId): ReactNode {
	const premiseIdValue = usePremiseId(premiseId);
	const url = getPremiseStatusURL(premiseIdValue);

	const [status, setStatus] = useState<PremiseStatusV2>();

	return (
		<>
			<PremiseStatusDisplayContent status={status} />
			<AuthenticatedJsonContent url={url} setData={setStatus} />
		</>
	);
}
